/**
 * THE STAFF PORTION OF A CALLER'S CARD, beneath the payments (Neelum, NYIA,
 * 2026-10-21).
 *
 * "a logistics box where Laura can input the date and time and location of
 * the job, plus any other details ... click each staff member's
 * corresponding button ... It would then text the staff member and ask them
 * to confirm if they can take the job. Once confirmed, the card should then
 * display the name(s) of the assigned investigators." And: "the button
 * turns orange for anyone she has reached out to (so she doesn't reach out
 * more than once) and then turns green when they have confirmed".
 *
 * So each staff member is one button: grey until asked, orange while the
 * desk waits, green when they said yes (by text, read off the line, or
 * marked here after a phone call), red when they said no. The wording of the
 * text is src/domain/staffjob.ts, shown in full before it goes. The list of
 * people is the admin's, edited in the Staff list box at the top of Calls.
 * Routes: server/routes/calldesk.ts.
 */

import { api, db } from "../data/client.ts";
import { esc } from "../ui/dom.ts";
import { notReady, offerText, NO_LOGISTICS, type Logistics } from "../domain/staffjob.ts";

export type Staff = { id: string; name: string; phone10: string; availability: string };
export type StaffAsk = { id: string; staff_id: string; asked_at: string; answer: "yes" | "no" | null;
  answered_at: string | null; answered_how: string | null };

/* The firm's people, from the list read, and each caller's job. */
let staff: Staff[] = [];
let firm = "";
const jobs = new Map<string, { name: string; logistics: Logistics; asks: StaffAsk[] }>();

export function setStaff(list: Staff[] | undefined, firmName?: string): void {
  if (list) staff = list;
  if (firmName !== undefined) firm = firmName;
}
export function setJob(p10: string, name: string, logistics: Logistics | null | undefined, asks: StaffAsk[] | undefined): void {
  jobs.set(p10, { name, logistics: logistics ?? { ...NO_LOGISTICS }, asks: asks ?? [] });
}

async function call<T>(path: string, payload?: unknown): Promise<T> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  const r = await fetch(api(path), {
    method: payload === undefined ? "GET" : "POST",
    headers: { Authorization: `Bearer ${session?.access_token ?? ""}`, "Content-Type": "application/json" },
    ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
  });
  const body = await r.json().catch(() => ({})) as T & { error?: string };
  if (!r.ok) throw new Error(body.error ?? "That did not go through. Try again.");
  return body;
}

const phoneOf = (p10: string): string => p10.replace(/^(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3");

const clock = (iso: string): string => {
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/** Who said yes, by name, in the order they were asked. */
export function assignedNames(p10: string): string[] {
  const j = jobs.get(p10);
  if (!j) return [];
  return j.asks.filter((a) => a.answer === "yes")
    .map((a) => staff.find((s) => s.id === a.staff_id)?.name ?? "").filter(Boolean);
}

/** The line at the top of the card, so who is on it shows without scrolling. */
export function assignedPill(p10: string): string {
  const names = assignedNames(p10);
  return `<span data-assigned-for="${esc(p10)}">${names.length
    ? ` <span class="pill cd-assigned-pill">Assigned: ${esc(names.join(", "))}</span>` : ""}</span>`;
}

function staffButton(p10: string, s: Staff): string {
  const a = jobs.get(p10)?.asks.find((x) => x.staff_id === s.id);
  const state = !a ? "" : a.answer === "yes" ? "is-yes" : a.answer === "no" ? "is-no" : "is-asked";
  const line = !a ? "Tap to ask"
    : a.answer === "yes" ? `Confirmed &#10003;${a.answered_at ? ` &middot; ${esc(clock(a.answered_at))}` : ""}`
    : a.answer === "no" ? "Can&#39;t take it"
    : `Asked ${esc(clock(a.asked_at))} &middot; waiting`;
  const actions = !a ? ""
    : a.answer === "yes" ? `<button type="button" class="cd-link" data-staff-undo="${esc(a.id)}">Undo</button>`
    : a.answer === "no" ? `<button type="button" class="cd-link" data-staff-again="${esc(s.id)}">Ask again</button>
        <button type="button" class="cd-link" data-staff-undo="${esc(a.id)}">Undo</button>`
    : `<button type="button" class="cd-link" data-staff-yes="${esc(a.id)}">Mark confirmed</button>
        <button type="button" class="cd-link" data-staff-no="${esc(a.id)}">Said no</button>
        <button type="button" class="cd-link" data-staff-undo="${esc(a.id)}">Undo</button>`;
  return `<div class="cd-person ${state}">
      <button type="button" class="cd-pbtn" data-staff-ask="${esc(s.id)}"${a ? ` aria-disabled="true"` : ""}
        aria-label="${esc(s.name)}: ${a ? line.replace(/&[^;]+;/g, " ") : "ask to take this job"}">
        <span class="cd-pname">${esc(s.name)}</span>
        <span class="cd-pstate">${line}</span>
        ${s.availability ? `<span class="cd-pavail">${esc(s.availability)}</span>` : ""}
      </button>
      ${actions ? `<div class="cd-pacts">${actions}</div>` : ""}
    </div>`;
}

export function staffHtml(p10: string): string {
  const j = jobs.get(p10);
  if (!j || !staff.length) return "";
  const l = j.logistics;
  const names = assignedNames(p10);
  return `<div class="cd-staff-head"><b>Staff</b>${names.length
      ? `<span class="cd-assigned">${names.map((n) => `<span class="cd-chip">${esc(n)}</span>`).join("")}</span>` : ""}</div>
    <div class="cd-logi" data-logi-box>
      <label class="cd-f"><span>Date</span><input type="date" data-logi="job_date" value="${esc(l.job_date)}"></label>
      <label class="cd-f"><span>Time</span><input type="time" data-logi="job_time" value="${esc(l.job_time)}"></label>
      <label class="cd-f cd-f-wide"><span>Location</span><input type="text" data-logi="location" value="${esc(l.location)}"
        placeholder="City or address" autocomplete="off"></label>
      <label class="cd-f cd-f-full"><span>Other details</span><textarea rows="2" data-logi="details"
        placeholder="What they need to know">${esc(l.details)}</textarea></label>
    </div>
    <div class="cd-staff-sub"><span class="fine quiet">Tap a name to text them the job. Orange is waiting, green is confirmed.</span></div>
    <div class="cd-people">${staff.map((s) => staffButton(p10, s)).join("")}</div>
    <div class="row" style="gap:.5rem;align-items:center;margin-top:.5rem;flex-wrap:wrap">
      ${j.asks.some((a) => !a.answer) ? `<button type="button" class="btn ghost sm" data-staff-check>Check for replies</button>` : ""}
      <span class="fine" data-staff-msg></span></div>`;
}

const msg = (box: HTMLElement, s: string) => { const m = box.querySelector<HTMLElement>("[data-staff-msg]"); if (m) m.textContent = s; };

function redraw(box: HTMLElement): void {
  const p10 = box.dataset["staffFor"] ?? "";
  /* Keep what is half typed in the box when the buttons redraw. */
  const typed = readLogi(box);
  const j = jobs.get(p10);
  if (j && typed) j.logistics = typed;
  box.innerHTML = staffHtml(p10);
  const top = document.querySelector<HTMLElement>(`[data-assigned-for="${CSS.escape(p10)}"]`);
  if (top) top.outerHTML = assignedPill(p10);
}

function readLogi(box: HTMLElement): Logistics | null {
  const v = (k: string) => box.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[data-logi="${k}"]`)?.value ?? null;
  const d = v("job_date");
  if (d === null) return null;
  return { job_date: d, job_time: v("job_time") ?? "", location: v("location") ?? "", details: v("details") ?? "" };
}

/** A logistics field was changed: all four save together. */
export async function saveLogistics(box: HTMLElement): Promise<boolean> {
  const p10 = box.dataset["staffFor"] ?? "";
  const j = jobs.get(p10);
  const l = readLogi(box);
  if (!j || !l) return false;
  if (JSON.stringify(l) === JSON.stringify(j.logistics)) return true;
  msg(box, "Saving…");
  try {
    const out = await call<{ logistics: Logistics }>("/api/calldesk/logistics", { phone10: p10, ...l });
    j.logistics = out.logistics;
    msg(box, "Saved.");
    return true;
  } catch (err) { msg(box, err instanceof Error ? err.message : String(err)); return false; }
}

export async function staffClick(box: HTMLElement, t: HTMLElement): Promise<void> {
  const p10 = box.dataset["staffFor"] ?? "";
  const j = jobs.get(p10);
  if (!j) return;
  const btns = Array.from(box.querySelectorAll<HTMLButtonElement>("button"));
  const busy = (on: boolean) => btns.forEach((b) => { b.disabled = on; });
  try {
    const ask = t.closest<HTMLButtonElement>("button[data-staff-ask]");
    const again = t.closest<HTMLButtonElement>("button[data-staff-again]");
    if ((ask && ask.getAttribute("aria-disabled") !== "true") || again) {
      const s = staff.find((x) => x.id === (ask?.dataset["staffAsk"] ?? again?.dataset["staffAgain"]));
      if (!s) return;
      if (!(await saveLogistics(box))) return;
      const bad = notReady(j.logistics);
      if (bad) { msg(box, bad); box.querySelector<HTMLInputElement>(`[data-logi="${j.logistics.job_date ? "location" : "job_date"}"]`)?.focus(); return; }
      const text = offerText({ firm, staffName: s.name, l: j.logistics });
      if (!confirm(`Text ${s.name} at ${phoneOf(s.phone10)}?\n\n${text}`)) return;
      busy(true); msg(box, `Texting ${s.name}…`);
      const out = await call<{ ask?: StaffAsk; warning?: string }>("/api/calldesk/staff/ask", { phone10: p10, staff_id: s.id, again: Boolean(again) });
      j.asks = j.asks.filter((a) => a.staff_id !== s.id);
      if (out.ask) j.asks.push(out.ask);
      redraw(box); msg(box, out.warning ?? `${s.name} was texted. Their button turns green when they reply YES.`);
      return;
    }
    const yes = t.closest<HTMLButtonElement>("button[data-staff-yes]");
    const no = t.closest<HTMLButtonElement>("button[data-staff-no]");
    if (yes || no) {
      busy(true);
      const out = await call<{ ask: StaffAsk }>("/api/calldesk/staff/answer", { id: (yes ?? no)!.dataset[yes ? "staffYes" : "staffNo"], answer: yes ? "yes" : "no" });
      j.asks = j.asks.map((a) => a.id === out.ask.id ? out.ask : a);
      redraw(box); return;
    }
    const undo = t.closest<HTMLButtonElement>("button[data-staff-undo]");
    if (undo) {
      if (!confirm("Undo this? Their button goes back to grey. The text that already went cannot be taken back.")) return;
      busy(true);
      await call("/api/calldesk/staff/undo", { id: undo.dataset["staffUndo"] });
      j.asks = j.asks.filter((a) => a.id !== undo.dataset["staffUndo"]);
      redraw(box); return;
    }
    if (t.closest("button[data-staff-check]")) {
      busy(true); msg(box, "Checking…");
      const out = await call<{ asks: StaffAsk[] }>(`/api/calldesk/staff/state?phone10=${p10}`);
      const before = j.asks.filter((a) => a.answer).length;
      j.asks = out.asks;
      redraw(box);
      msg(box, j.asks.filter((a) => a.answer).length > before ? "New replies are in." : "No new replies yet.");
      return;
    }
  } catch (err) {
    busy(false);
    msg(box, err instanceof Error ? err.message : String(err));
  }
}

/**
 * THE STAFF LIST, at the top of Calls next to Payment info, for admins:
 * "the list of staff ... will change as we hire more people". Name, number,
 * when they are free. Taking someone off keeps the texts already sent.
 */
export async function staffEditor(host: HTMLElement): Promise<void> {
  let st: { staff: Staff[]; may_edit: boolean };
  try { st = await call("/api/calldesk/staff"); } catch { host.hidden = true; return; }
  if (!st.may_edit) { host.hidden = true; return; }
  host.hidden = false;
  const rowHtml = (s: Partial<Staff>) => `<div class="cd-erow" data-staff-row="${esc(s.id ?? "")}">
      <label class="cd-f"><span>Name</span><input type="text" data-se="name" value="${esc(s.name ?? "")}" autocomplete="off"></label>
      <label class="cd-f"><span>Phone</span><input type="tel" inputmode="tel" data-se="phone" value="${esc(phoneOf(s.phone10 ?? ""))}" autocomplete="off"></label>
      <label class="cd-f cd-f-wide"><span>Availability</span><input type="text" data-se="availability" value="${esc(s.availability ?? "")}" autocomplete="off"></label>
      <button type="button" class="btn ghost danger sm cd-eremove" data-se-remove aria-label="Take ${esc(s.name ?? "this person")} off the list">Remove</button>
    </div>`;
  host.innerHTML = `<details class="card pad cd-staffedit">
    <summary class="card-head" style="cursor:pointer">Staff list (${st.staff.length})</summary>
    <p class="fine quiet">The people a job can go to. Each caller's Staff section has a button for everyone here. Only admins see this box.</p>
    <div data-se-rows>${st.staff.map(rowHtml).join("")}</div>
    <div class="row" style="gap:.4rem;align-items:center;flex-wrap:wrap;margin-top:.5rem">
      <button type="button" class="btn ghost sm" data-se-add>+ Add staff member</button>
      <button type="button" class="btn primary sm" data-se-save>Save staff list</button>
      <span class="fine" data-se-msg></span></div>
  </details>`;
  const rows = host.querySelector<HTMLElement>("[data-se-rows]")!;
  const m = host.querySelector<HTMLElement>("[data-se-msg]")!;
  host.addEventListener("click", async (e) => {
    const t = e.target as HTMLElement;
    if (t.closest("[data-se-add]")) { rows.insertAdjacentHTML("beforeend", rowHtml({})); rows.querySelector<HTMLInputElement>(".cd-erow:last-child input")?.focus(); return; }
    const rm = t.closest<HTMLElement>("[data-se-remove]");
    if (rm) { rm.closest(".cd-erow")?.remove(); m.textContent = "Save the list to make it stick."; return; }
    const save = t.closest<HTMLButtonElement>("[data-se-save]");
    if (!save) return;
    const list = Array.from(rows.querySelectorAll<HTMLElement>(".cd-erow")).map((r) => ({
      id: r.dataset["staffRow"] || undefined,
      name: r.querySelector<HTMLInputElement>('[data-se="name"]')!.value,
      phone: r.querySelector<HTMLInputElement>('[data-se="phone"]')!.value,
      availability: r.querySelector<HTMLInputElement>('[data-se="availability"]')!.value,
    })).filter((s) => s.name.trim() || s.phone.trim());
    save.disabled = true; m.textContent = "Saving…";
    try {
      const out = await call<{ staff: Staff[] }>("/api/calldesk/staff", { staff: list });
      setStaff(out.staff);
      rows.innerHTML = out.staff.map(rowHtml).join("");
      host.querySelector("summary")!.textContent = `Staff list (${out.staff.length})`;
      document.querySelectorAll<HTMLElement>("[data-staff-for]").forEach(redraw);
      m.textContent = "Saved.";
    } catch (err) { m.textContent = err instanceof Error ? err.message : String(err); }
    save.disabled = false;
  });
}

